import { Modal, SaveBar, TitleBar } from "@shopify/app-bridge-react"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

import TPAPI from "@/api/tracking-page"
import {
  AppWindowSaveBarMessage,
  AppWindowSaveBarState,
  createAppWindowSaveBarChannel,
  isAppWindowSaveBarMessage,
} from "@/component/SaveModule/appWindowSaveBarBridge"
import { ThemeLayoutEnum } from "@/models/ITrackingPageOptions"
import {
  buildThemeLayoutAppWindowSrc,
  THEME_LAYOUT_BACKDROP_APP_WINDOW_ID,
  THEME_LAYOUT_SHOWCASE_APP_WINDOW_ID,
} from "@/pages/TrackingPage/DisplayOptions/components/themeLayoutAppWindow"
import toast from "@/utils/toast"

const TRACKING_PAGE_DEFAULT_PATH = "/tracking-page/appearance"

const INITIAL_SAVE_BAR_STATE: AppWindowSaveBarState = {
  show: false,
  loading: false,
  disabled: false,
  unlocking: false,
}

export interface ThemeLayoutModalHostProps {
  subPath: "customizeBackdrop" | "customizeShowcase"
}

const getModalId = (subPath: ThemeLayoutModalHostProps["subPath"]) => {
  return subPath === "customizeBackdrop"
    ? THEME_LAYOUT_BACKDROP_APP_WINDOW_ID
    : THEME_LAYOUT_SHOWCASE_APP_WINDOW_ID
}

const getThemeVariant = (subPath: ThemeLayoutModalHostProps["subPath"]) => {
  return subPath === "customizeBackdrop"
    ? ThemeLayoutEnum.BACKDROP
    : ThemeLayoutEnum.SHOWCASE
}

/**
 * App Bridge React `<Modal variant="max">` 版本的 customize 弹窗壳，作为 `<s-app-window>` 备用方案。
 *
 * 选 Modal 而非 s-app-window 的原因：
 * - `onHide` 是 React prop，比 `addEventListener("hide", ...)` 触发更可靠；
 * - `<SaveBar>` 作为 `<Modal>` 子节点时，modal 关闭会自动隐藏 SaveBar，不会出现"窗口已关但 SaveBar 还在"。
 *
 * iframe 内 SaveBar 状态仍通过 BroadcastChannel 广播给父 frame；父 frame 根据状态控制
 * `<SaveBar open>`，用户点 Save/Discard 反向广播给 iframe 触发业务回调。
 */
const ThemeLayoutModalHost = ({ subPath }: ThemeLayoutModalHostProps) => {
  const navigate = useNavigate()
  const { search } = useLocation()
  const { t } = useTranslation(["common", "tracking_page"])
  const [saveBarState, setSaveBarState] = useState<AppWindowSaveBarState>(INITIAL_SAVE_BAR_STATE)
  const [previewUrl, setPreviewUrl] = useState("")
  const channelRef = useRef<BroadcastChannel | null>(null)
  const themeVariant = getThemeVariant(subPath)

  const handleHide = useCallback(() => {
    navigate(TRACKING_PAGE_DEFAULT_PATH, { replace: true })
  }, [navigate])

  useEffect(() => {
    const channel = createAppWindowSaveBarChannel()

    if (!channel) {
      return
    }
    channelRef.current = channel

    const handleMessage = (event: MessageEvent<unknown>) => {
      const { data } = event

      if (!isAppWindowSaveBarMessage(data)) {
        return
      }
      if (data.type === "state") {
        setSaveBarState(data.payload)
      }
    }

    channel.addEventListener("message", handleMessage)
    const requestMessage: AppWindowSaveBarMessage = { type: "request-state" }

    channel.postMessage(requestMessage)

    return () => {
      channel.removeEventListener("message", handleMessage)
      channel.close()
      channelRef.current = null
      setSaveBarState(INITIAL_SAVE_BAR_STATE)
    }
  }, [])

  useEffect(() => {
    let isUnmounted = false

    const fetchThemePreview = async () => {
      try {
        const { data } = await TPAPI.getThemePreview(themeVariant)

        if (isUnmounted) {
          return
        }
        setPreviewUrl(data.tracking_page_theme_preview.url)
      } catch {
        if (isUnmounted) {
          return
        }
        setPreviewUrl("")
      }
    }

    void fetchThemePreview()

    return () => {
      isUnmounted = true
    }
  }, [themeVariant])

  const handlePreviewThisTheme = useCallback(() => {
    if (previewUrl) {
      window.open(previewUrl, "_blank", "noopener,noreferrer")
      return
    }
    toast(String(t("tracking_page:previewThisTheme")), 2e3)
  }, [previewUrl, t])

  const handleSave = useCallback(() => {
    const channel = channelRef.current

    if (!channel) {
      return
    }
    const message: AppWindowSaveBarMessage = { type: "action", payload: "save" }

    channel.postMessage(message)
  }, [])

  const handleDiscard = useCallback(() => {
    const channel = channelRef.current

    if (!channel) {
      return
    }
    const message: AppWindowSaveBarMessage = { type: "action", payload: "discard" }

    channel.postMessage(message)
  }, [])

  const src = buildThemeLayoutAppWindowSrc(subPath, search)
  const id = getModalId(subPath)
  const saveBarId = `${id}-save-bar`
  const isSaveBarOpen = saveBarState.show && !saveBarState.unlocking
  const saveDisabled = saveBarState.disabled || saveBarState.loading
  const saveLabel = String(t("common:Save"))
  const discardLabel = String(t("common:Discard"))
  const previewLabel = String(t("tracking_page:previewThisTheme"))
  const title = String(t(
    subPath === "customizeBackdrop"
      ? "tracking_page:customizeBackdrop"
      : "tracking_page:customizeShowcase",
  ))

  return (
    <Modal id={id} variant="max" src={src} open onHide={handleHide}>
      <TitleBar title={title}>
        {!isSaveBarOpen ? (
          <button variant="primary" onClick={handlePreviewThisTheme}>
            {previewLabel}
          </button>
        ) : null}
      </TitleBar>
      <SaveBar id={saveBarId} open={isSaveBarOpen}>
        {/* `<button>` 在 SaveBar 内被 Shopify 注册为 ui-button，需要非标准属性 variant / loading；用 block disable 让多行 attribute 都豁免 lint */}
        {/* eslint-disable react/button-has-type, react/no-unknown-property */}
        <button
          variant="primary"
          onClick={handleSave}
          loading={saveBarState.loading ? "loading" : undefined}
          disabled={saveDisabled || undefined}
        >
          {saveLabel}
        </button>
        <button onClick={handleDiscard}>
          {discardLabel}
        </button>
        {/* eslint-enable react/button-has-type, react/no-unknown-property */}
      </SaveBar>
    </Modal>
  )
}

export default ThemeLayoutModalHost
